import { useRef, useState } from "react";
import {
  ExternalLink,
  Eye,
  Pencil,
  Plus,
  Trash2,
  Table2,
  Download,
  ChevronDown,
  ChevronUp,
  RefreshCw,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import apiClient from "@/api/client";
import { Spinner } from "@/components/ui/Spinner";
import { SpreadsheetPreviewModal } from "./SpreadsheetPreviewModal";
import {
  useCreateTaskBackup,
  useCreateSubTaskBackup,
} from "@/features/tasks/hooks/useSpreadsheetLinks";
import type { SpreadsheetLink } from "@/features/tasks/types";

// ── SharePoint last-modified meta ─────────────────────────────────────────────

interface SharePointFileInfo {
  modified_by: string;
  modified_at: string;
}

function useSharePointMeta(embedCode: string, src: string | null) {
  return useQuery<SharePointFileInfo | null>({
    queryKey: ["sharepoint-file-info", embedCode],
    queryFn: async () => {
      const resp = await apiClient.get<{ data: SharePointFileInfo }>(
        "/tasks/sharepoint-file-info/",
        { params: { embed_code: embedCode } },
      );
      return resp.data.data;
    },
    enabled: !!src && src.includes("sharepoint.com"),
    staleTime: 1000 * 60 * 5,
    retry: false,
  });
}

function fmtDate(iso: string): string {
  if (!iso) return "";
  return new Date(iso).toLocaleString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function SpreadsheetMeta({ embedCode }: { embedCode: string }) {
  const src = extractIframeSrc(embedCode);
  const { data } = useSharePointMeta(embedCode, src);
  if (!data?.modified_by) return null;
  return (
    <span className="text-xs text-gray-400 dark:text-gray-500 truncate min-w-0">
      {data.modified_by} · {fmtDate(data.modified_at)}
    </span>
  );
}

// ── Backup history ────────────────────────────────────────────────────────────

interface Backup {
  id: string;
  file_name: string;
  file_size: number;
  created_at: string;
  download_url: string | null;
}

function useBackups(url: string, enabled: boolean) {
  return useQuery<Backup[]>({
    queryKey: ["spreadsheet-backups", url],
    queryFn: async () => {
      const resp = await apiClient.get<{ data: Backup[] }>(url);
      return resp.data.data;
    },
    enabled,
    staleTime: 1000 * 60 * 5,
    retry: false,
  });
}

function BackupHistory({ backupUrl }: { backupUrl: string }) {
  const [open, setOpen] = useState(false);
  const { data: backups = [], isLoading } = useBackups(backupUrl, open);

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 mt-0.5"
      >
        <Download size={11} />
        Historia backupów
        <ChevronDown size={11} />
      </button>
    );
  }

  return (
    <div className="mt-1">
      <button
        onClick={() => setOpen(false)}
        className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 mb-1"
      >
        <Download size={11} />
        Historia backupów
        <ChevronUp size={11} />
      </button>
      {isLoading ? (
        <span className="text-xs text-gray-400">Ładowanie…</span>
      ) : backups.length === 0 ? (
        <span className="text-xs text-gray-400 italic">
          Brak backupów (pierwsze wykonanie o 03:00)
        </span>
      ) : (
        <div className="flex flex-col gap-0.5 pl-1 border-l-2 border-gray-200 dark:border-gray-700">
          {backups.map((b) => (
            <div key={b.id} className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400 w-32 flex-shrink-0">
                {fmtDate(b.created_at)}
              </span>
              <span className="text-xs text-gray-400 w-14 flex-shrink-0">
                {fmtSize(b.file_size)}
              </span>
              {b.download_url ? (
                <a
                  href={b.download_url}
                  download={b.file_name}
                  className="flex items-center gap-1 text-xs text-[var(--color-primary)] hover:underline"
                >
                  <Download size={11} />
                  {b.file_name}
                </a>
              ) : (
                <span className="text-xs text-gray-300">{b.file_name}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main exports ──────────────────────────────────────────────────────────────

export function extractIframeSrc(embedCode: string): string | null {
  const match = embedCode.match(/src=["']([^"']+)["']/i);
  return match ? match[1] : null;
}

export function deriveEditUrl(embedCode: string): string | null {
  const src = extractIframeSrc(embedCode);
  if (!src) return null;

  if (/docs\.google\.com\/spreadsheets\/d\/e\//.test(src)) return null;

  const gsMatch = src.match(
    /docs\.google\.com\/spreadsheets\/d\/([a-zA-Z0-9_-]{20,})/,
  );
  if (gsMatch)
    return `https://docs.google.com/spreadsheets/d/${gsMatch[1]}/edit`;

  if (src.includes("sharepoint.com") || src.includes("office.com"))
    return src.replace("action=embedview", "action=default");

  return src;
}

interface Props {
  links: SpreadsheetLink[];
  isLoading: boolean;
  onCreate: (data: { name: string; embed_code: string }) => Promise<unknown>;
  onDelete: (id: string) => void;
  onRename?: (id: string, name: string) => Promise<unknown>;
  onUpdate?: (id: string, data: { name: string; embed_code: string }) => Promise<unknown>;
  isCreating?: boolean;
  taskId: string;
  subtaskId?: string;
}

export function SpreadsheetLinkList({
  links,
  isLoading,
  onCreate,
  onDelete,
  onRename,
  onUpdate,
  isCreating,
  taskId,
  subtaskId,
}: Props) {
  const [preview, setPreview] = useState<SpreadsheetLink | null>(null);
  const [name, setName] = useState("");
  const [embedCode, setEmbedCode] = useState("");
  const [formError, setFormError] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const editCanceledRef = useRef(false);
  const [editLink, setEditLink] = useState<{ id: string; name: string; embed_code: string } | null>(null);
  const [editError, setEditError] = useState("");
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const {
    mutate: createTaskBackup,
    isPending: taskBackupPending,
    variables: taskBackupLinkId,
  } = useCreateTaskBackup(taskId);
  const {
    mutate: createSubTaskBackup,
    isPending: subBackupPending,
    variables: subBackupLinkId,
  } = useCreateSubTaskBackup(taskId, subtaskId ?? "");

  function handleBackup(linkId: string) {
    if (subtaskId) createSubTaskBackup(linkId);
    else createTaskBackup(linkId);
  }

  function isBackingUp(linkId: string) {
    if (subtaskId) return subBackupPending && subBackupLinkId === linkId;
    return taskBackupPending && taskBackupLinkId === linkId;
  }

  function startEditing(link: SpreadsheetLink) {
    editCanceledRef.current = false;
    setEditingId(link.id);
    setEditingName(link.name);
  }

  async function handleRenameBlur(id: string) {
    if (editCanceledRef.current) {
      editCanceledRef.current = false;
      setEditingId(null);
      return;
    }
    setEditingId(null);
    const trimmed = editingName.trim();
    const original = links.find((l) => l.id === id)?.name;
    if (trimmed && trimmed !== original) {
      await onRename?.(id, trimmed);
    }
  }

  function handleRenameKeyDown(e: React.KeyboardEvent<HTMLInputElement>, _id: string) {
    if (e.key === "Enter") {
      e.preventDefault();
      e.currentTarget.blur();
    } else if (e.key === "Escape") {
      editCanceledRef.current = true;
      e.currentTarget.blur();
    }
  }

  function startEditLink(link: SpreadsheetLink) {
    setEditLink({ id: link.id, name: link.name, embed_code: link.embed_code });
    setEditError("");
  }

  async function handleSaveEdit() {
    if (!editLink) return;
    const trimmedName = editLink.name.trim();
    const trimmedCode = editLink.embed_code.trim();
    if (!trimmedName) {
      setEditError("Podaj nazwę arkusza.");
      return;
    }
    if (!trimmedCode) {
      setEditError("Wklej kod iframe.");
      return;
    }
    if (!extractIframeSrc(trimmedCode)) {
      setEditError("Nie znaleziono atrybutu src w podanym kodzie.");
      return;
    }
    setEditError("");
    setIsSavingEdit(true);
    try {
      await onUpdate?.(editLink.id, { name: trimmedName, embed_code: trimmedCode });
      setEditLink(null);
    } finally {
      setIsSavingEdit(false);
    }
  }

  async function handleAdd() {
    const trimmedName = name.trim();
    const trimmedCode = embedCode.trim();
    if (!trimmedName) {
      setFormError("Podaj nazwę arkusza.");
      return;
    }
    if (!trimmedCode) {
      setFormError("Wklej kod iframe z SharePoint.");
      return;
    }
    if (!extractIframeSrc(trimmedCode)) {
      setFormError(
        "Nie znaleziono atrybutu src w podanym kodzie. Upewnij się, że wklejasz pełny kod <iframe ...>.",
      );
      return;
    }
    setFormError("");
    await onCreate({ name: trimmedName, embed_code: trimmedCode });
    setName("");
    setEmbedCode("");
  }

  function backupUrl(linkId: string): string {
    if (subtaskId)
      return `/tasks/${taskId}/subtasks/${subtaskId}/spreadsheet-links/${linkId}/backups/`;
    return `/tasks/${taskId}/spreadsheet-links/${linkId}/backups/`;
  }

  if (isLoading)
    return (
      <div className="flex justify-center py-6">
        <Spinner />
      </div>
    );

  return (
    <>
      {links.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500 italic py-2">
          Brak podpiętych arkuszy.
        </p>
      ) : (
        <div className="flex flex-col gap-2 mb-4">
          {links.map((link) => {
            const editUrl = deriveEditUrl(link.embed_code);
            const backing = isBackingUp(link.id);
            return (
              <div
                key={link.id}
                className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50"
              >
                <div className="flex flex-col sm:flex-row sm:items-start gap-2">
                  <div className="flex items-start gap-2 flex-1 min-w-0">
                    <Table2
                      size={15}
                      className="flex-shrink-0 text-green-600 dark:text-green-500 mt-0.5"
                    />
                    <div className="flex flex-col flex-1 min-w-0">
                      {editingId === link.id ? (
                        <input
                          autoFocus
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          onBlur={() => handleRenameBlur(link.id)}
                          onKeyDown={(e) => handleRenameKeyDown(e, link.id)}
                          className="text-sm font-medium text-gray-800 dark:text-gray-100 bg-transparent border-b border-[var(--color-primary)] outline-none w-full"
                        />
                      ) : (
                        <span
                          className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate cursor-text select-none"
                          onDoubleClick={() => onRename && startEditing(link)}
                          title={onRename ? "Kliknij dwukrotnie, aby zmienić nazwę" : undefined}
                        >
                          {link.name}
                        </span>
                      )}
                      <SpreadsheetMeta embedCode={link.embed_code} />
                      <BackupHistory backupUrl={backupUrl(link.id)} />
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0 flex-wrap justify-end sm:justify-start">
                    <button
                      onClick={() => setPreview(link)}
                      title="Podgląd"
                      className="flex items-center gap-1 text-xs text-[var(--color-primary)] hover:underline px-1.5 py-1 rounded hover:bg-[var(--color-primary-dim)]"
                    >
                      <Eye size={13} />
                      Podgląd
                    </button>
                    {editUrl && (
                      <a
                        href={editUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Otwórz do edycji"
                        className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 px-1.5 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <ExternalLink size={13} />
                        Otwórz
                      </a>
                    )}
                    <button
                      onClick={() => handleBackup(link.id)}
                      disabled={backing}
                      title="Stwórz backup teraz"
                      className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 px-1.5 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40"
                    >
                      {backing ? (
                        <Spinner size="sm" />
                      ) : (
                        <RefreshCw size={13} />
                      )}
                      Backup
                    </button>
                    {onUpdate && (
                      <button
                        onClick={() => startEditLink(link)}
                        title="Edytuj arkusz"
                        className="p-1 text-gray-400 hover:text-[var(--color-primary)] rounded transition-colors"
                      >
                        <Pencil size={13} />
                      </button>
                    )}
                    <button
                      onClick={() => onDelete(link.id)}
                      title="Usuń"
                      className="p-1 text-gray-400 hover:text-red-500 rounded sm:opacity-0 sm:hover:opacity-100 transition-opacity"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
                {editLink?.id === link.id && (
                  <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600 flex flex-col gap-2">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Edytuj arkusz
                    </p>
                    <input
                      type="text"
                      placeholder="Nazwa arkusza"
                      value={editLink.name}
                      onChange={(e) => setEditLink({ ...editLink, name: e.target.value })}
                      className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] bg-white dark:bg-gray-700 dark:text-gray-100"
                    />
                    <textarea
                      placeholder="Wklej kod <iframe> z SharePoint"
                      value={editLink.embed_code}
                      onChange={(e) => setEditLink({ ...editLink, embed_code: e.target.value })}
                      rows={3}
                      className="w-full text-xs font-mono border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] bg-white dark:bg-gray-700 dark:text-gray-100 resize-none"
                    />
                    {editError && <p className="text-xs text-red-500">{editError}</p>}
                    <div className="flex gap-2">
                      <button
                        onClick={handleSaveEdit}
                        disabled={isSavingEdit}
                        className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-50 transition-opacity"
                      >
                        {isSavingEdit ? <Spinner size="sm" /> : null}
                        Zapisz
                      </button>
                      <button
                        onClick={() => setEditLink(null)}
                        className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        Anuluj
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add form */}
      <div className="border-t border-gray-100 dark:border-gray-700 pt-3">
        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
          Dodaj arkusz
        </p>
        <div className="flex flex-col gap-2">
          <input
            type="text"
            placeholder="Nazwa arkusza"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] bg-white dark:bg-gray-700 dark:text-gray-100"
          />
          <textarea
            placeholder={
              "Wklej kod <iframe> z SharePoint - MS Excel 365 (Plik → Udostępnij → Osadź / File → Share → Embed this workbook)"
            }
            value={embedCode}
            onChange={(e) => setEmbedCode(e.target.value)}
            rows={3}
            className="w-full text-xs font-mono border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] bg-white dark:bg-gray-700 dark:text-gray-100 resize-none"
          />

          {formError && <p className="text-xs text-red-500">{formError}</p>}
          <button
            onClick={handleAdd}
            disabled={isCreating}
            className="flex items-center gap-1.5 self-start text-sm px-3 py-1.5 rounded-lg bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-50 transition-opacity"
          >
            {isCreating ? <Spinner size="sm" /> : <Plus size={14} />}
            Dodaj
          </button>
        </div>
      </div>

      {preview && (
        <SpreadsheetPreviewModal
          name={preview.name}
          embedCode={preview.embed_code}
          onClose={() => setPreview(null)}
        />
      )}
    </>
  );
}
